import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ContentWrapper } from '../../Style'
import CommunicationInstructions from '../Communicationassmt/instruction'
import AptitudeInstructions from '../aptitudeassmt/instruction'
import CustomInstructions from '../customassmt/instruction'
import BehaviourInstructions from '../Behaviourassmt/instruction'
import RoleBasedInstructions from '../RoleBasedassmt/instruction'
import {
  fetchAssessmentQuestions,
  saveQuestionResponse,
  fetchCommunicationInstructions,
  checkInviteReloadGuard,
} from '../../actions'
import selectors from '../../selectors'
import { INVITE_SESSION_KEYS } from '../AIInterview/aiInterviewInviteAPI'

// Map the resolve/verify assessmentType to the `type` value the instruction
// switch expects. Identity for most; kept explicit so unexpected values fail
// loudly rather than silently rendering the wrong runner.
const TYPE_MAP = {
  Communication: 'Communication',
  // Hinglish is NOT a real assessment type — it is Communication with
  // response_language=Hinglish (STT language lives on the map, not a separate
  // flow). Fold it onto the Communication runner so any already-sent invite
  // token still opens the real assessment instead of the legacy hardcoded
  // 20-min "Hinglish Assessment" fork.
  Hinglish: 'Communication',
  Aptitude: 'Aptitude',
  Custom_Assessment: 'Custom_Assessment',
  Behavior: 'Behavior',
  Role_Based: 'Role_Based',
}

const centeredPanel = (children) => (
  <ContentWrapper>
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '2rem',
      }}
    >
      <div style={{ maxWidth: 420 }}>{children}</div>
    </div>
  </ContentWrapper>
)

const InviteAssessmentRunner = () => {
  const { assignedId } = useParams()
  const dispatch = useDispatch()

  const [completed, setCompleted] = useState(false)
  // Reload guard: 'checking' until the backend reports the attempt state.
  // 'pending'/'resume' -> render instructions; 'dropout'/'completed' -> terminal.
  const [guardState, setGuardState] = useState('checking')

  // Read invite context from sessionStorage (written by InviteStart on verify).
  // setId lives here rather than the URL so the URL matches the AI Interview
  // shape (one UUID, not two).
  const setId =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.assessmentSetId)
      : null
  const sessionType =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.assessmentType)
      : null
  const sessionRole =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.role)
      : null
  const sessionAllowProctoring =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.allowProctoring)
      : null
  const sessionAllowVerification =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.allowVerification)
      : null
  const sessionName =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.name)
      : null

  const mappedType = sessionType ? TYPE_MAP[sessionType] || sessionType : null
  const allowProctoring = sessionAllowProctoring === 'true'
  // Opt-out: on unless the invite session explicitly stored 'false'. A missing
  // key (older session) keeps the biometric check, matching prior behaviour.
  const allowVerification = sessionAllowVerification !== 'false'
  const inviteStudentId =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.studentId)
      : null
  const sessionEndTime =
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(INVITE_SESSION_KEYS.endTime)
      : null
  // Prefer the real assessment name; role only applies to AI Interview (which
  // launches via its own screen, not this runner), so fall back to a generic.
  const title = sessionName || sessionRole || 'Assessment'

  // Build the assessment object the instruction components consume.
  const assessment = useMemo(
    () => ({
      assessment_assigned_id: assignedId,
      assessment_set_id: setId,
      type: mappedType,
      title,
      allowProctoring,
      allowVerification,
      studentId: inviteStudentId,
      endTime: sessionEndTime,
      isCorporate: true,
      isPractice: false,
    }),
    [assignedId, setId, mappedType, title, allowProctoring, allowVerification, sessionEndTime]
  )

  // Redux selectors (same set DirectAssessment wires).
  const questions = useSelector(selectors.getQuestions)
  const totalQuestions = useSelector(selectors.getTotalQuestions)
  const loading = useSelector(selectors.getLoading)
  const error = useSelector(selectors.getError)
  const responses = useSelector(selectors.getResponses)

  // Seed the current-assessment id in Redux so downstream API calls carry the
  // right ids (mirrors DirectAssessment's SET_CURRENT_ASSESSMENT_ID dispatch).
  useEffect(() => {
    if (!mappedType || !assignedId || !setId) return
    dispatch({
      type: 'SET_CURRENT_ASSESSMENT_ID',
      payload: {
        assessment_id: assignedId,
        assessment_set_id: setId,
        assessment_assigned_id: assignedId,
        allowProctoring,
        allowVerification,
        studentId: inviteStudentId,
        title,
        type: mappedType,
        isPractice: false,
      },
    })
  }, [dispatch, mappedType, assignedId, setId, allowProctoring, allowVerification, title])

  // On mount (incl. every reload / re-entry of this runner) ask the backend what
  // to render. A reload of an already-started, non-resumable attempt is marked
  // DROPOUT server-side and returns 'dropout' — so the candidate sees a terminal
  // screen instead of the instruction page and cannot retake. First entry is
  // still PENDING -> 'pending' -> instructions.
  useEffect(() => {
    if (!assignedId || !sessionType) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const state = await checkInviteReloadGuard(assignedId)
        if (!cancelled) setGuardState(state)
      } catch (_) {
        // Fail open: a transient guard error must not lock out a legitimate first
        // attempt. The backend start-guard still blocks an actual retake.
        if (!cancelled) setGuardState('pending')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [assignedId, sessionType])

  // Memoized dispatch callbacks (same signatures DirectAssessment passes).
  const dispatchFetchAssessmentQuestions = useCallback(
    (assessmentAssignedId) =>
      dispatch(fetchAssessmentQuestions(assessmentAssignedId)),
    [dispatch]
  )

  const dispatchSaveQuestionResponse = useCallback(
    (questionId, answer, assessmentAssignedId, subQuestionId, questionType) =>
      dispatch(
        saveQuestionResponse(
          questionId,
          answer,
          assessmentAssignedId,
          subQuestionId,
          questionType
        )
      ),
    [dispatch]
  )

  const dispatchFetchCommunicationInstructions = useCallback(
    (assessmentSetId) => dispatch(fetchCommunicationInstructions(assessmentSetId)),
    [dispatch]
  )

  // setAssessment handler: instruction components call this with `null` when
  // the candidate finishes or backs out. Clear OTP-flow sessionStorage and
  // hard-redirect to /assessment so the candidate lands on the assessment
  // home (mirrors the AI Interview "Back to Home" path).
  const handleSetAssessment = useCallback((value) => {
    if (value) return
    try {
      Object.values(INVITE_SESSION_KEYS).forEach((k) =>
        sessionStorage.removeItem(k)
      )
      // legacy AI-interview keys (defensive — runner is non-AI, but cleanup all)
      sessionStorage.removeItem('ai_interview_scoped_jwt')
      sessionStorage.removeItem('ai_interview_assessment_assigned_id')
    } catch (_) {
      /* no-op */
    }
    setCompleted(true)
    try {
      window.location.href = '/assessment'
    } catch (_) {
      /* no-op */
    }
  }, [])

  // proctoring uses assignedId as the student_id fallback (same convention as
  // the AI-interview OTP flow, where there is no portal student id).
  const studentId = assignedId

  if (completed) {
    return centeredPanel(
      <>
        <h2 style={{ marginBottom: 8 }}>You&apos;re all set</h2>
        <p style={{ color: '#64748b' }}>
          Your response has been submitted. You can close this window.
        </p>
      </>
    )
  }

  // Direct hit without verifying — no invite context present.
  if (!sessionType || !mappedType) {
    return centeredPanel(
      <>
        <h2 style={{ marginBottom: 8 }}>Open from your invite email</h2>
        <p style={{ color: '#64748b' }}>
          This assessment link needs to be opened from your invite email.
        </p>
      </>
    )
  }

  // Reload guard verdict (see the mount effect above).
  if (guardState === 'checking') {
    return centeredPanel(
      <p style={{ color: '#64748b' }}>Loading…</p>
    )
  }

  if (guardState === 'dropout') {
    return centeredPanel(
      <>
        <h2 style={{ marginBottom: 8 }}>Assessment closed</h2>
        <p style={{ color: '#64748b' }}>
          This assessment was already started and can be attempted only once.
          Reloading or leaving the page ends the attempt, so it can’t be taken
          again. Please contact the recruiter if you believe this is a mistake.
        </p>
      </>
    )
  }

  if (guardState === 'completed') {
    return centeredPanel(
      <>
        <h2 style={{ marginBottom: 8 }}>Already submitted</h2>
        <p style={{ color: '#64748b' }}>
          You’ve already completed this assessment. Your responses have been
          recorded — there’s nothing more to do here. You can close this window.
        </p>
      </>
    )
  }

  const commonProps = {
    assessment,
    questions,
    totalQuestions,
    loading,
    error,
    responses,
    fetchAssessmentQuestions: dispatchFetchAssessmentQuestions,
    saveQuestionResponse: dispatchSaveQuestionResponse,
    studentId,
    setAssessment: handleSetAssessment,
  }

  switch (mappedType) {
    case 'Communication':
      return (
        <CommunicationInstructions
          {...commonProps}
          fetchCommunicationInstructions={dispatchFetchCommunicationInstructions}
        />
      )
    case 'Aptitude':
      return <AptitudeInstructions {...commonProps} />
    case 'Custom_Assessment':
      return <CustomInstructions {...commonProps} />
    case 'Behavior':
      return <BehaviourInstructions {...commonProps} />
    case 'Role_Based':
      return <RoleBasedInstructions {...commonProps} />
    default:
      return centeredPanel(
        <>
          <h2 style={{ marginBottom: 8 }}>Unsupported assessment</h2>
          <p style={{ color: '#64748b' }}>
            Assessment type &quot;{mappedType}&quot; is not supported here.
          </p>
        </>
      )
  }
}

export default InviteAssessmentRunner
